import { JetView } from 'webix-jet';
import {
    devConfig, // Development configuration settings
    countries, // List of countries for selection
    profile, // User profile settings
    cashDesks, // Cash desk configurations
    agentList, // List of agents
} from 'config/config';

/**
 * Class representing a filter view for cash flow summary.
 * @extends JetView
 */
export default class FilterFlujoCajaResumidoView extends JetView {
    /**
     * Configures the UI components of the view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization function
        const screen = this.app.config.size; // Current screen size

        // Left section containing filters
        const left = {
            margin: 5,
            id: 'a1',
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a1',
                    cols: [
                        {
                            view: 'text', // Datepicker input
                            name: 'Datepicker',
                            id: 'Datepicker',
                            css: 'reportrange',
                            minWidth: 200,
                            value:
                                moment().subtract(0, 'days').format('YYYY-MM-DD') +
                                ' - ' +
                                moment().format('YYYY-MM-DD'), // Default date range
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Label for datepicker
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                        },
                        {
                            view: 'richselect', // Country selection dropdown
                            name: 'CountrySelect',
                            id: 'CountrySelect',
                            minWidth: 200,
                            localId: 'regions:combo',
                            label: "<span class='icon-language'></span>" + _('País'), // Label for country select
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            options: {
                                body: {
                                    template: function (obj) {
                                        return "<i class='flags " + obj.icon + "'></i>" + obj.value; // Country option template
                                    },
                                    data: countries, // Data source for countries
                                },
                            },
                            hidden: devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'), // Conditional visibility
                            css: 'prm-filterByCountry',
                        },
                        {
                            view: 'richselect', // Read-only country selection
                            name: 'CountrySelect2',
                            minWidth: 200,
                            localId: 'regions:combo',
                            label: "<span class='icon-language'></span>" + _('País'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            value: profile.Settings.ReportCountry, // Default value from profile
                            readonly: true,
                            options: {
                                body: {
                                    template: function (obj) {
                                        return "<i class='flags " + obj.icon + "'></i>" + obj.value; // Country option template
                                    },
                                    data: countries, // Data source for countries
                                },
                            },
                            hidden: !devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'), // Conditional visibility
                            css: 'prm-filterByCountry',
                        },
                        {
                            view: 'text', // User ID input
                            name: 'UserId',
                            type: 'number',
                            minWidth: 200,
                            label: "<span class='icon-drivers-license-o'></span>" + _('ID Usuario'), // Label for user ID
                            hidden: !devConfig.getPermAction('filterByUser', 'Add'), // Conditional visibility
                            css: 'prm-filterByUser',
                            labelPosition: 'top',
                            placeholder: _('ID Usuario'), // Placeholder text
                        },
                    ],
                },
                {
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a1',
                    cols: [
                        {
                            view: 'radio', // Radio button for report type
                            name: 'Type',
                            id: 'Type',
                            label: _('Tipo'), // Label for type selection
                            labelPosition: 'top',
                            minWidth: 200,
                            height: document.body.offsetWidth < 600 ? 100 : 0,
                            value: 1, // Default value
                            options: [
                                { id: 0, value: _('Detallado') }, // Detailed option
                                { id: 1, value: _('Totales') }, // Total option
                            ],
                        },
                        {
                            view: 'radio', // Radio button for payment gateways
                            name: 'WithPaymentGateways',
                            id: 'WithPaymentGateways',
                            label: _('Con pasarelas de pago?'), // Label for payment gateways
                            labelPosition: 'top',
                            minWidth: 200,
                            height: document.body.offsetWidth < 600 ? 100 : 0,
                            value: 1, // Default value
                            options: [
                                { id: 0, value: _('Si') }, // Yes option
                                { id: 1, value: _('No') }, // No option
                            ],
                        },
                        {
                            view: 'radio', // Radio button for canceled bets
                            name: 'WithCanceledBets',
                            id: 'WithCanceledBets',
                            label: _('Con apuestas anuladas?'), // Label for canceled bets
                            labelPosition: 'top',
                            minWidth: 200,
                            height: document.body.offsetWidth < 600 ? 100 : 0,
                            value: 1, // Default value
                            options: [
                                { id: 0, value: _('Si') }, // Yes option
                                { id: 1, value: _('No') }, // No option
                            ],
                        },
                        {
                            view: 'radio', // Radio button for pending awards
                            name: 'WithPendingAwards',
                            id: 'WithPendingAwards',
                            label: _('Con premios pendientes?'), // Label for pending awards
                            labelPosition: 'top',
                            minWidth: 200,
                            height: document.body.offsetWidth < 600 ? 100 : 0,
                            value: 1, // Default value
                            options: [
                                { id: 0, value: _('Si') }, // Yes option
                                { id: 1, value: _('No') }, // No option
                            ],
                        },
                    ],
                },
            ],
        };

        // Button section for actions
        const buttons = {
            margin: 10,
            cols: [
                {},
                {
                    view: 'button', // Clear button
                    value: _('Limpiar'),
                    width: 100,
                    click: () => {
                        $$('myForm').setValues($$('myForm').getCleanValues()); // Reset form values
                        $$('CountrySelect').setValue($$('myForm').getCleanValues().CountrySelect); // Reset country selection
                        $$('Datepicker').setValue(''); // Clear datepicker
                    },
                },
                {
                    view: 'button', // Consult button
                    value: _('Consultar'),
                    hotkey: 'enter', // Shortcut key
                    type: 'form',
                    width: 110,
                    id: 'consultDesk',
                    click: () => {
                        const filterdata = this.getRoot().getValues(); // Get form values
                        if (devConfig.getCountryReport()) {
                            filterdata.CountrySelect = profile.Settings.ReportCountry; // Set country based on profile
                        }
                        delete filterdata['CountrySelect2']; // Remove unnecessary field
                        this.app.callEvent('flujoCajaResumido:filter', [filterdata]); // Trigger filter event
                        $$('b5').collapse(); // Collapse filter section
                        $$('a5').scrollTo(100, 0); // Scroll to specific position
                    },
                },
            ],
        };

        // Upper section containing filters and buttons
        const upper_section = {
            css: 'father-container',
            rows: [
                {
                    margin: 10,
                    cols: [left], // Include left section
                },
            ],
        };

        // Accordion section for mobile view
        const accordion_section2 = {
            type: 'wide',
            rows: [
                {
                    multi: true,
                    view: 'accordion',
                    css: 'betShop',
                    collapsed: true,
                    type: 'clean',
                    rows: [
                        {
                            view: 'button', // Consult button for mobile
                            value: _('Consultar'),
                            hotkey: 'enter', // Shortcut key
                            type: 'form',
                            width: screen == 'small' ? 0 : 100,
                            minWidth: 200,
                            id: 'consultMobile',
                            click: () => {
                                const filterdata = this.getRoot().getValues(); // Get form values
                                if (devConfig.getCountryReport()) {
                                    filterdata.CountrySelect = profile.Settings.ReportCountry; // Set country based on profile
                                }
                                delete filterdata['CountrySelect2']; // Remove unnecessary field
                                this.app.callEvent('flujoCajaResumido:filter', [filterdata]); // Trigger filter event
                                $$('b5').collapse(); // Collapse filter section
                                $$('a5').scrollTo(100, 0); // Scroll to specific position
                            },
                        },
                        {
                            header: _('Filtros'), // Header for filters
                            id: 'b5',
                            body: {
                                padding: 10,
                                rows: [upper_section, buttons], // Include upper section and buttons
                            },
                        },
                    ],
                },
            ],
        };

        // Return the main form configuration
        return {
            view: 'form',
            id: 'myForm',
            type: 'clean',
            rows: [accordion_section2], // Include accordion section
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule
            },
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     * @param {Object} grid - The grid component.
     */
    init(grid) {
        const lang = this.app.getService('locale').getLang(); // Get current language
        $$('Datepicker').attachEvent('onAfterRender', function () {
            devConfig.setDateModal(lang); // Set date modal based on language
        });
        webix.ready(function () {
            if (screen.width < 500) {
                $$('consultDesk').hide(); // Hide desktop consult button on small screens
            }
            if (screen.width >= 500) {
                $$('consultMobile').hide(); // Hide mobile consult button on larger screens
            }
        });
    }
}

// FILE DOCUMENTED
